import React from 'react';
import ReactDOM from 'react-dom/client';
import AppRouterFixed from './AppRouterFixed.tsx';
import './App.css';

// Error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-red-500 text-6xl mb-4">💥</div>
            <h1 className="text-2xl font-bold text-white mb-4">Oops! Something went wrong</h1>
            <p className="text-gray-300 mb-6">
              The poker client encountered an unexpected error. Please refresh the page to try again.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Reload Page
              </button>
              <details className="text-left">
                <summary className="text-gray-400 text-sm cursor-pointer hover:text-gray-300">
                  Show Error Details
                </summary>
                <pre className="mt-2 p-2 bg-gray-800 text-red-400 text-xs rounded overflow-auto max-h-32">
                  {this.state.error?.stack || this.state.error?.message || 'Unknown error'}
                </pre>
              </details>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Initialize React app
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppRouterFixed />
    </ErrorBoundary>
  </React.StrictMode>
);

// Service worker registration removed to prevent MIME type errors

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  
  // You could also send this to an error tracking service
  if (event.reason?.message?.includes('crypto') || event.reason?.message?.includes('WebRTC')) {
    console.warn('Crypto or WebRTC error - may be browser compatibility issue');
  }
});

// Performance monitoring (development only)
if (import.meta.env.DEV) {
  // Log initial page load performance
  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      console.log('Page Load Performance:', {
        totalTime: perfData.loadEventEnd - perfData.fetchStart,
        domContentLoaded: perfData.domContentLoadedEventEnd - perfData.fetchStart,
        domInteractive: perfData.domInteractive - perfData.fetchStart,
      });
    }, 0);
  });
}